import AppGlobalSettings from "@app/AppGlobalSettings"
import { getInfoForCurrentUser } from "@ng-mw/framework-core"

const middagstipsBffBaseUrls = {
    local: "https://localhost:7268/",
    preproduction: "https://kiwibff.trumffrontend.systest.trumf.cloud/",
    production: "https://kiwibff.trumffrontend.prod.trumf.cloud/",
} as const

interface MiddagstipsBffBaseUrlOptions {
    appEnvironment?: string
    nodeEnv?: string
}

interface MiddagstipsBffRequestOptions {
    method: "GET" | "POST" | "PUT" | "DELETE"
    path: string
    body?: unknown
    query?: Record<string, string>
}

export interface MiddagstipsBffResponseError {
    status: number
    statusText: string
    body: unknown
}

export const getMiddagstipsBffBaseUrl = ({
    appEnvironment = AppGlobalSettings.environment,
    nodeEnv = process.env.NODE_ENV,
}: MiddagstipsBffBaseUrlOptions = {}) => {
    if (appEnvironment?.toString() === "3") {
        return middagstipsBffBaseUrls.production
    }

    if (nodeEnv !== "production" || appEnvironment?.toString() === "2") {
        // Return middagstipsBffBaseUrls.local when running the app locally against a local BFF.
        return middagstipsBffBaseUrls.preproduction
    }

    return middagstipsBffBaseUrls.production
}

const getMiddagstipsBffUrl = (path: string, query?: Record<string, string>) => {
    const url = new URL(path, getMiddagstipsBffBaseUrl())

    if (query) {
        Object.entries(query).forEach(([key, value]) => {
            url.searchParams.set(key, value)
        })
    }

    return url.toString()
}

const tryParseResponseBody = async (response: Response) => {
    const contentType = response.headers.get("content-type")

    if (contentType?.includes("json")) {
        return response.json().catch(() => undefined)
    }

    return response.text().catch(() => undefined)
}

export const performMiddagstipsBffRequest = async <TResponse = unknown>({
    method,
    path,
    body,
    query,
}: MiddagstipsBffRequestOptions): Promise<TResponse> => {
    const { userToken } = await getInfoForCurrentUser()
    const response = await fetch(getMiddagstipsBffUrl(path, query), {
        method,
        headers: {
            Authorization: userToken,
            "Content-Type": "application/json",
        },
        body: body === undefined ? undefined : JSON.stringify(body),
    })

    if (response.ok) {
        if (response.status === 204) return undefined as TResponse

        return tryParseResponseBody(response) as Promise<TResponse>
    }

    throw {
        status: response.status,
        statusText: response.statusText,
        body: await tryParseResponseBody(response),
    } satisfies MiddagstipsBffResponseError
}
